import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { type ReactNode, useCallback, useState } from 'react'
import { cn } from '@/lib/utils'

/** A slot the user can drop a card onto. */
export interface DroppableSlotProps {
  id: string
  disabled?: boolean
  className?: string
  /** isOver treatment merged in by the component; children render the day body */
  children: ReactNode
}

export function DroppableSlot({
  id,
  disabled,
  className,
  children,
}: DroppableSlotProps): ReactNode {
  const { setNodeRef, isOver } = useDroppable({ id, disabled })
  return (
    <div ref={setNodeRef} className={cn('relative rounded-lg', className)}>
      {children}
      {/* Drop-target cue: a faint blue wash over the whole cell. Painted as an
          overlay above the children because slot bodies (e.g. curriculum day
          columns) fill themselves with an opaque background that would hide
          any background set on this wrapper. */}
      <div
        aria-hidden
        data-testid="drop-target-wash"
        className={cn(
          'pointer-events-none absolute inset-0 rounded-[inherit] bg-[oklch(0.55_0.13_235)]/15 transition-opacity duration-150 dark:bg-[#5ab8e8]/12',
          isOver ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}

/** A card the user can pick up. */
export interface DraggableCardProps {
  id: string
  disabled?: boolean
  /**
   * Opaque payload attached to the drag (echoed back through @dnd-kit's own
   * `event.active.data`). CardBoard's callbacks pass only the id, so consumers
   * re-derive card details from their own state rather than this payload.
   */
  data?: Record<string, unknown>
  className?: string
  /** rendered 40% opacity while it is the active drag source */
  isDragSource?: boolean
  children: ReactNode
}

export function DraggableCard({
  id,
  disabled,
  data,
  className,
  isDragSource,
  children,
}: DraggableCardProps): ReactNode {
  const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef } =
    useDraggable({
      id,
      data,
      disabled,
    })
  // The whole card is the drag surface (grab anywhere). A disabled card gets
  // neither listeners nor dnd-kit's role/tabIndex, so it is not draggable and
  // not a focusable dead "draggable" tab stop. A small pointer-move threshold
  // (PointerSensor activation distance) keeps clicks on inner controls from
  // starting a drag.
  const dragProps = disabled ? {} : { ...listeners, ...attributes }
  // The card is also its own keyboard activator. `listeners` puts the
  // KeyboardSensor's `onKeyDown` on this wrapper, and the sensor only declines
  // a keydown that came from somewhere other than the activator node — but
  // only when one is registered. With `activatorNode` left null the guard is
  // skipped entirely, so Enter or Space on any nested control (a card's
  // details, trash, or convert button) bubbled up here, called
  // `preventDefault()`, and started a keyboard drag instead of pressing the
  // button — the pointer path worked, so this was only ever broken for
  // keyboard users. Registering the wrapper makes the sensor's own check
  // meaningful: a keydown on the card itself still lifts it, one from a nested
  // button no longer does. Pointer dragging is untouched — `PointerSensor`'s
  // activator never consults this node, so grab-anywhere still holds.
  const setRefs = useCallback(
    (node: HTMLElement | null): void => {
      setNodeRef(node)
      setActivatorNodeRef(node)
    },
    [setActivatorNodeRef, setNodeRef],
  )
  return (
    <div
      ref={setRefs}
      {...dragProps}
      className={cn(
        'touch-none',
        !disabled && 'cursor-grab',
        // Dim the lifted card so the floating overlay reads as *the* card,
        // not a duplicate. Consumers may still force it via `isDragSource`
        // (e.g. a highlight that must persist through the drop animation).
        (isDragSource || isDragging) && 'opacity-40',
        className,
      )}
    >
      {children}
    </div>
  )
}

const DROP_GLIDE_MS = 200
// How many frames the drop animation waits for the consumer's state update
// to land the card in its destination slot. Synchronous consumers (homework)
// settle on the first frame; cache-backed consumers (curriculum) notify on a
// macrotask (TanStack Query defers subscriber notifications), which a couple
// of frames comfortably cover. A rejected drop never moves the card, so the
// wait caps out and the overlay glides back home.
const DROP_SETTLE_FRAMES = 8

const nextFrame = (): Promise<unknown> =>
  new Promise((resolve) => requestAnimationFrame(resolve))

/**
 * Pure glide computation — exported for deterministic unit testing. Given the
 * overlay's current transform and rect plus the card's settled rect, returns
 * the WAAPI keyframes that carry the overlay onto the card, or null when it
 * is already there (sub-pixel drift is not worth an animation).
 */
export function dropGlideKeyframes(
  transform: { x: number; y: number },
  overlayRect: { left: number; top: number },
  targetRect: { left: number; top: number },
): [Keyframe, Keyframe] | null {
  const dx = targetRect.left - overlayRect.left
  const dy = targetRect.top - overlayRect.top
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null
  return [
    { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` },
    {
      transform: `translate3d(${transform.x + dx}px, ${transform.y + dy}px, 0)`,
    },
  ]
}

/**
 * Drop animation that waits for the drop to *settle* before measuring where
 * to send the overlay. @dnd-kit's default animation measures the card the
 * instant the drag ends — for consumers whose drop handler updates state
 * asynchronously (a react-query cache write notifies subscribers on a
 * macrotask), the card is still in its source slot at that moment, so the
 * overlay flies back to where the drag started and the card later teleports.
 * Waiting a few frames lets an accepted drop land first, so the overlay
 * glides into the destination slot; a rejected drop leaves the card in
 * place and the glide returns it home.
 */
const settleThenGlide: DropAnimation = async ({
  active,
  dragOverlay,
  draggableNodes,
  measuringConfiguration,
  transform,
}) => {
  const measure = measuringConfiguration.draggable.measure
  const initial = active.rect
  let node: HTMLElement | null = null
  for (let frame = 0; frame < DROP_SETTLE_FRAMES; frame++) {
    await nextFrame()
    node = draggableNodes.get(active.id)?.node.current ?? null
    if (!node || !node.isConnected) continue
    const rect = measure(node)
    if (rect.left !== initial.left || rect.top !== initial.top) break
  }
  if (!node || !node.isConnected) return
  const keyframes = dropGlideKeyframes(
    transform,
    dragOverlay.rect,
    measure(node),
  )
  if (!keyframes) return
  // Hide the settled card while the overlay glides onto it, so the drop reads
  // as one card arriving rather than two briefly coexisting.
  const previousOpacity = node.style.opacity
  node.style.opacity = '0'
  try {
    await dragOverlay.node.animate(keyframes, {
      duration: DROP_GLIDE_MS,
      easing: 'ease',
      fill: 'forwards',
    }).finished
  } finally {
    node.style.opacity = previousOpacity
  }
}

/** Pure drop resolution — exported for deterministic unit testing. */
export function resolveDrop(
  activeId: string | null,
  overId: string | null,
): { cardId: string; slotId: string } | null {
  if (!activeId || !overId) return null
  return { cardId: activeId, slotId: overId }
}

export interface CardBoardProps {
  /** fires when a card is dropped on a slot; slot === over.id, card === active.id */
  onPlace: (cardId: string, slotId: string) => void
  /**
   * fired when a drag starts, before onPlace; consumers use it to cancel
   * any armed click-to-place so two pending placements cannot coexist
   */
  onDragStartCard?: (cardId: string) => void
  /** render the floating overlay for the active card id (null when none) */
  renderOverlay: (cardId: string) => ReactNode
  children: ReactNode
}

export function CardBoard({
  onPlace,
  onDragStartCard,
  renderOverlay,
  children,
}: CardBoardProps): ReactNode {
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id)
      setActiveId(id)
      onDragStartCard?.(id)
    },
    [onDragStartCard],
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      const resolved = resolveDrop(
        event.active.id != null ? String(event.active.id) : null,
        event.over?.id != null ? String(event.over.id) : null,
      )
      if (resolved) onPlace(resolved.cardId, resolved.slotId)
    },
    [onPlace],
  )

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {children}
      <DragOverlay dropAnimation={settleThenGlide}>
        {activeId ? (
          <div className="animate-card-pickup cursor-grabbing">
            {renderOverlay(activeId)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
